import React from 'react';
import { ExternalLink } from 'lucide-react';
import { FormSection } from '../../common/ui';

const RoleBadge = ({ label, color }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
    {label}
  </span>
);

const AccountCard = ({ account, roles }) => {
  const name = [account.first_name, account.last_name].filter(Boolean).join(' ') || account.email;
  return (
    <div className="py-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-gray-900">{name}</span>
        {roles.map((r) => (
          <RoleBadge key={r.label} label={r.label} color={r.color} />
        ))}
        <a
          href={`https://admin.seeyou.cloud/users/${account.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-300 hover:text-blue-500 transition-colors"
          title="View on SeeYou Cloud"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
      {account.email && (
        <div className="text-xs text-gray-500 mt-0.5">{account.email}</div>
      )}
    </div>
  );
};

const OwnerInfoSection = ({ ownership, ownershipLoading = false, deviceData }) => {
  const hasLegacy = deviceData && (deviceData.owner_name || deviceData.owner_email);

  const renderContent = () => {
    if (ownershipLoading) {
      return (
        <div className="flex items-center gap-2 text-sm text-gray-400 italic py-2">
          <svg className="animate-spin h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading ownership data…
        </div>
      );
    }

    const hasLive =
      ownership &&
      (ownership.owner || ownership.warranty || (ownership.using && ownership.using.length > 0));

    if (!hasLive && !hasLegacy) {
      return (
        <div className="text-sm text-gray-400 italic py-2">No ownership information available</div>
      );
    }

    // Build merged map: one entry per unique account id with all their roles
    const byId = {};

    if (ownership?.owner) {
      const u = ownership.owner;
      byId[u.id] = { account: u, isOwner: true, isWarranty: false, isUsing: false };
    }

    if (ownership?.warranty) {
      const u = ownership.warranty;
      const existing = byId[u.id];
      byId[u.id] = {
        account: u,
        isOwner: existing?.isOwner ?? false,
        isWarranty: true,
        isUsing: existing?.isUsing ?? false,
      };
    }

    if (ownership?.using) {
      for (const u of ownership.using) {
        const existing = byId[u.id];
        byId[u.id] = {
          account: u,
          isOwner: existing?.isOwner ?? false,
          isWarranty: existing?.isWarranty ?? false,
          isUsing: true,
        };
      }
    }

    // Sort: owner > warranty > using
    const sorted = Object.values(byId).sort((a, b) => {
      const scoreA = (a.isOwner ? 4 : 0) + (a.isWarranty ? 2 : 0) + (a.isUsing ? 1 : 0);
      const scoreB = (b.isOwner ? 4 : 0) + (b.isWarranty ? 2 : 0) + (b.isUsing ? 1 : 0);
      return scoreB - scoreA;
    });

    return (
      <div className="space-y-1">
        {hasLive && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 divide-y md:divide-y-0 divide-gray-100">
            {sorted.map(({ account, isOwner, isWarranty, isUsing }) => {
              const roles = [];
              if (isOwner) roles.push({ label: 'Owner', color: 'bg-blue-100 text-blue-800' });
              if (isWarranty) {
                const warrantySince = account.warranty_since;
                const label = warrantySince
                  ? `Warranty since ${warrantySince.slice(0, 10)}`
                  : 'Warranty Holder';
                roles.push({ label, color: 'bg-green-100 text-green-800' });
              }
              if (isUsing && !isOwner && !isWarranty) {
                roles.push({ label: 'Using only', color: 'bg-gray-100 text-gray-600' });
              }
              return <AccountCard key={account.id} account={account} roles={roles} />;
            })}
          </div>
        )}

        {hasLegacy && (
          <div className="mt-3 pt-3 border-t border-dashed border-gray-200">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">
              Legacy (database record)
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {deviceData.owner_name && (
                <div className="flex items-start gap-1">
                  <span className="text-gray-500 font-medium whitespace-nowrap">Name:</span>
                  <span className="text-gray-700 italic">{deviceData.owner_name}</span>
                </div>
              )}
              {deviceData.owner_email && (
                <div className="flex items-start gap-1">
                  <span className="text-gray-500 font-medium whitespace-nowrap">Email:</span>
                  <span className="text-gray-700 italic">{deviceData.owner_email}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <FormSection title="Owner Information">
      {renderContent()}
    </FormSection>
  );
};

export default OwnerInfoSection;
